import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import {
    Button,
    Checkbox,
    Popover,
    Typography,
    makeStyles
} from '@material-ui/core';
import FlagIcon from '@material-ui/icons/Flag';

import OverflowTypography from '../../../../Common/UI/OverflowTypography';
import { AreaText } from '../../../../Common/UI/AreaText';
import { DEFAULT_RISKS } from '../../../../Common/Config/Risks';
import ConstraintPopover from './ConstraintPopover';

const propTypes = {
    id: PropTypes.number.isRequired, // Id of feature analysis
    siteCard: PropTypes.object, // Optional
    data: PropTypes.object.isRequired,

    isSelected: PropTypes.bool.isRequired,
    onHoverEnter: PropTypes.func.isRequired,
    onHoverLeave: PropTypes.func.isRequired,
    onZoomFeature: PropTypes.func.isRequired,

    onSelectCard: PropTypes.func.isRequired,
    onSaveSiteCard: PropTypes.func.isRequired,
    onViewSiteCard: PropTypes.func.isRequired
};

const defaultProps = {
    siteCard: null
};

const useStyles = makeStyles((theme) => styles(theme));

function AnalysisCard ({
    id,
    siteCard,
    data,
    isSelected,
    onHoverEnter,
    onHoverLeave,
    onZoomFeature,
    onSelectCard,
    onSaveSiteCard,
    onViewSiteCard
}) {
    const primary = data.feature.primaryLabel;
    const secondary = data.feature.secondaryLabel;
    const area = data.feature.area;
    const chip = DEFAULT_RISKS.find(risk => risk.label === data.weightings.risk);
    const classes = useStyles();

    const [anchorEl, setAnchorEl] = useState(null);

    function handlePopoverOpen (event) {
        if (data.weightings.risk !== 'Very Low Risk') {
            setAnchorEl(event.currentTarget);
        }  
    }

    function handlePopoverClose () {
        setAnchorEl(null);
    }
    
    function handleChecked (event) {
        onSelectCard(id, event.target.checked);
    }

    function handleOnMouseEnter () {
        onHoverEnter(id);
    }

    function handleOnMouseLeave () {
        onHoverLeave(id);
    }

    function handleSaveSiteCard () {
        if (!siteCard) { onSaveSiteCard(id, ''); }
    }

    function handleViewSiteCard () {
        // ToDo Update analysis on site card if already exists
        if (siteCard) { onViewSiteCard(); }
    }

    function handleZoom () {
        onZoomFeature(id);
    }

    return (
        <div
            className={classes.root}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
        >
            <div className={classes.checkboxContainer}>
                <Checkbox
                    checked={isSelected}
                    onChange={handleChecked}
                />
            </div>
            <div className={classes.text} onClick={handleZoom}>
                <div className={classes.title}>
                    {siteCard && <FlagIcon color="secondary" />}
                    <OverflowTypography
                        className={classes.name}
                        color={siteCard ? 'secondary' : 'textPrimary'}
                        variant="subtitle1"
                    >
                        {siteCard ? siteCard.name : primary}
                    </OverflowTypography>
                    <Typography color="textPrimary" variant="body2">
                        <AreaText area={area} />
                    </Typography>
                </div>
                <OverflowTypography color="textPrimary" variant="body2">
                    {secondary}
                </OverflowTypography>
            </div>
            <Typography
                className={classes.chip}
                variant="body2"
                style={{
                    backgroundColor: chip.backgroundColor,
                    color: chip.color
                }}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
            >
                {chip.label}
            </Typography>
            <ConstraintPopover
                data={data}
                anchorEl={anchorEl}
                onPopoverClose={handlePopoverClose}
            />
            <div>
                {
                    siteCard ?
                        // <Link to={`/dashboard/sites`}>
                        <Link className={classes.link} to={`/dashboard/sites/${siteCard.id}`}>
                            <Button
                                className={classes.buttonLink}
                                variant="contained"
                                onClick={() => {}}
                            >
                                View
                            </Button>
                        </Link> :
                        <Button
                            className={classes.button}
                            color="secondary"
                            variant="contained"
                            onClick={handleSaveSiteCard}
                        >
                            Save
                        </Button>
                }
            </div>

        </div>
    );
}

const styles = theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        borderRadius: 5,
        border: `1px solid ${theme.palette.background.border}`,
        '&:hover': {
            border: `1px solid ${theme.palette.secondary.light}`,
            boxShadow: `0px 0px 0px 1px ${theme.palette.secondary.light} inset`
        }
    },
    checkboxContainer: {
        display: 'flex',
        alignSelf: 'stretch',
        alignItems: 'center',
        backgroundColor: `${theme.palette.grey.variant5}1A`
    },
    text: {
        minWidth: 0,
        padding: 10,
        flexGrow: 1,
        '&:hover': {
            cursor: 'pointer'
        }
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 5,
        '& > *:last-child':{
            marginLeft: 'auto'
        }
    },
    chip: {
        borderRadius: 10,
        padding: '5px 10px',
        whiteSpace: 'nowrap',
        alignSelf: 'center',
        minWidth: 100,
        textAlign: 'center'
    },
    button: {
        margin: '0px 10px',
        whiteSpace: 'nowrap',
        ...theme.typography.body2,
        padding: '5px 10px',
        borderRadius: 10
    },
    buttonLink: {
        margin: '0px 10px',
        whiteSpace: 'nowrap',
        ...theme.typography.body2,
        padding: '5px 10px',
        borderRadius: 10,
        backgroundColor: theme.palette.primary.light
    },
    link: {
        textDecoration: 'none'
    },
    popover: {
        pointerEvents: 'none',
    },
    paper: {
        padding: 5
    }
});

AnalysisCard.propTypes = propTypes;
AnalysisCard.defaultProps = defaultProps;

export default AnalysisCard;